import React, { useEffect, useCallback, useRef, type MutableRefObject } from 'react';
import { createPortal } from 'react-dom';

export const usePortal = (
  isOpen: boolean,
  onClose: () => void,
  portalPosition: { top: number; left: number },
  zIndex: number = 1000,
  hostRef?: React.MutableRefObject<HTMLDivElement | null>
) => {
  const portalRef = useRef<HTMLDivElement | null>(null) as MutableRefObject<HTMLDivElement | null>;

  const assignPortalRef = useCallback(
    (node: HTMLDivElement | null) => {
      portalRef.current = node;
      if (hostRef) {
        hostRef.current = node;
      }
    },
    [hostRef]
  );

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (portalRef.current && !portalRef.current.contains(event.target as Node)) {
        onClose();
      }
    },
    [onClose]
  );

  const handleEscapeKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleClickOutside, handleEscapeKey]);

  const renderPortal = useCallback(
    (children: React.ReactNode) => {
      if (!isOpen) return null;

      return createPortal(
        React.createElement(
          'div',
          {
            ref: assignPortalRef,
            style: {
              position: 'fixed',
              top: `${portalPosition.top}px`,
              left: `${portalPosition.left}px`,
              zIndex: zIndex,
            },
            className:
              'date-picker-portal ui-bg-fill ui-rounded-lg ui-shadow-xl ui-border ui-border-default',
          },
          children
        ),
        document.body
      );
    },
    [isOpen, portalPosition, zIndex, assignPortalRef]
  );

  return {
    portalRef,
    renderPortal,
  };
};
